import React from "react"

// components
import {
  Button,
  Modal,
  Image,
  Typography,
  InputNumber,
  Space,
  Divider,
  Avatar,
  Form,
  Checkbox,
  Radio,
  Row,
} from "antd"
import { MinusOutlined, PlusOutlined } from "@ant-design/icons"

// store
import { useTranslation } from "react-i18next"

// configs
import config from "config"

// utils
import { formatAsCurrency } from "utils/currency"

const MenuItemModal = ({ open, onCancel, item, onAdd = () => null }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage

  const name = item?.name_i18n?.[lang] ?? item?.name
  const info = item?.info_i18n?.[lang] ?? item?.info

  const [form] = Form.useForm()

  return (
    <Modal
      data-testid="menu-item-modal"
      style={{ top: "20%" }}
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          {name}
        </Typography.Title>
      }
      footer={null}
      open={open}
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
    >
      {item?.hasImage && (
        <>
          <Row justify="center" style={{ marginBottom: 16 }}>
            <Image
              src={`restos/${config.key}/menu/${String(
                item?.id
              ).toLowerCase()}.png`}
              alt={`img-${item?.id}`}
              preview={false}
              width={200}
            />
          </Row>
          <Divider />
        </>
      )}

      <Typography.Title
        data-testid="menu-item-price"
        level={4}
        type="secondary"
        style={{ fontStyle: "italic", margin: 0, marginBottom: 12 }}
      >
        {item?.preis !== undefined
          ? formatAsCurrency(item?.preis)
          : item?.preis}
      </Typography.Title>
      <Typography.Paragraph>{info}</Typography.Paragraph>
      {item?.details && (
        <Typography.Paragraph italic type="secondary">
          {item.details}
        </Typography.Paragraph>
      )}
      {(item?.scharf || 0) > 0 && (
        <Typography.Paragraph
          type="secondary"
          italic
          style={{ marginBottom: 0 }}
        >
          {item?.scharf === 2 ? (
            <span>
              <Avatar
                src="icons/chili.png"
                size={16}
                alt="icon-chili"
                style={{ marginRight: "-10px" }}
              />
              <Avatar src="icons/chili.png" size={16} alt="icon-chili" />
            </span>
          ) : item?.scharf === 1 ? (
            <Avatar src="icons/chili.png" size={16} alt="icon-chili" />
          ) : null}{" "}
          {t("spicy")}
        </Typography.Paragraph>
      )}
      {item?.vegetarisch && (
        <Typography.Paragraph type="secondary" italic>
          <Avatar src="icons/leaf.png" size={16} alt="icon-leaf" />{" "}
          {t("vegetarian")}
        </Typography.Paragraph>
      )}

      {item?.settings && <Divider />}

      <Form
        requiredMark={false}
        form={form}
        size="small"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        onFinish={(values) => {
          const amount = values?.amount || 1

          const choices = item?.settings
            ? item?.settings.reduce((acc, set) => {
                if (set.type === "checkbox" || set.type === "radio") {
                  const value = values[set.key]
                  if (value !== undefined) {
                    acc[set.key] = Array.isArray(value)
                      ? value.map((v) => v)
                      : value
                  }
                }
                return acc
              }, {})
            : null

          onAdd({ ...item, choices }, amount)
          form.resetFields()
          onCancel()
        }}
      >
        {item?.settings &&
          item.settings.map((set) => {
            switch (set.type) {
              case "checkbox":
                return (
                  <Form.Item
                    key={set.key}
                    label={set.label_i18n?.[lang] ?? set.label}
                    name={set.key}
                    style={{ marginBottom: 4 }}
                    initialValue={set.default}
                    rules={[
                      {
                        required: set.required,
                        message: t("please_select_option"),
                      },
                    ]}
                  >
                    <Checkbox.Group
                      options={set.options.map((opt) => {
                        const newOpt = { ...opt }
                        newOpt.label = `${
                          newOpt.label_i18n?.[lang] ?? newOpt.label
                        } ${
                          newOpt.preis
                            ? `(+${formatAsCurrency(newOpt.preis)})`
                            : ""
                        }`
                        return newOpt
                      })}
                    />
                  </Form.Item>
                )

              case "radio":
                return (
                  <Form.Item
                    key={set.key}
                    label={set.label_i18n?.[lang] ?? set.label}
                    name={set.key}
                    style={{ marginBottom: 4 }}
                    initialValue={set.default}
                    rules={[
                      {
                        required: set.required,
                        message: t("please_select_option"),
                      },
                    ]}
                  >
                    <Radio.Group
                      size="small"
                      options={set.options.map((opt) => {
                        const newOpt = { ...opt }
                        newOpt.label = `${
                          newOpt.label_i18n?.[lang] ?? newOpt.label
                        } ${
                          newOpt.preis
                            ? `(+${formatAsCurrency(newOpt.preis)})`
                            : ""
                        }`
                        return newOpt
                      })}
                    />
                  </Form.Item>
                )

              default:
                return null
            }
          })}

        <Row justify="end" style={{ marginTop: 20 }}>
          <Space>
            <Space.Compact>
              <Button
                data-testid="dec-amount-button"
                icon={<MinusOutlined />}
                onClick={() =>
                  form.setFieldsValue({
                    amount: Math.max(1, form.getFieldValue("amount") - 1),
                  })
                }
              />

              <Form.Item
                name="amount"
                noStyle
                initialValue={1}
                rules={[
                  {
                    required: true,
                    message: t("please_enter_amount"),
                  },
                ]}
              >
                <InputNumber
                  data-testid="amount-input"
                  min={1}
                  style={{ width: 30 }}
                  controls={false}
                />
              </Form.Item>

              <Button
                data-testid="inc-amount-button"
                icon={<PlusOutlined />}
                onClick={() =>
                  form.setFieldsValue({
                    amount: form.getFieldValue("amount") + 1,
                  })
                }
              />
            </Space.Compact>

            {item?.preis !== undefined && (
              <Form.Item shouldUpdate noStyle>
                {(form) => {
                  let total = item?.preis
                  if (item?.settings) {
                    const settings = form.getFieldsValue(true)
                    Object.entries(settings).forEach(([key, value]) => {
                      const settingsValues = item.settings.find(
                        (s) => s.key === key
                      )
                      if (settingsValues?.options.find((opt) => opt.preis)) {
                        const selectedOptions = Array.isArray(value)
                          ? value
                          : [value]
                        selectedOptions.forEach((opt) => {
                          const option = item.settings
                            .find((s) => s.key === key)
                            .options.find((o) => o.value === opt)
                          if (option) {
                            total += option.preis || 0
                          }
                        })
                      }
                    })
                  }

                  total *= form.getFieldValue("amount") || 1

                  return (
                    <Button
                      htmlType="submit"
                      size="default"
                      data-testid="add-to-order-button"
                      type="primary"
                    >
                      {t("add")} ({formatAsCurrency(total)})
                    </Button>
                  )
                }}
              </Form.Item>
            )}
          </Space>
        </Row>
      </Form>
    </Modal>
  )
}

export default MenuItemModal
